import React, { useCallback } from 'react';
import InfiniteViewer from 'react-infinite-viewer';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import {
  attachmentsSelector,
  elementsToRenderSelector,
  selectedSlideSelector,
  useDesignerDataContext,
} from '../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../hooks/useDesignerUIState';
import { zIndexes } from '../../../utils/zIndexes';
import ProxySlideEditorSurface from '../../SlideComponents/ProxySlideEditorSurface';
import { SlideElement } from '../../SlideComponents/slide.types';
import SlideEditorToolbar from '../../SlideComponents/SlideEditorToolbar';
const ASPECT = { width: 1920, height: 1080 };
export type ElementTransform = {
  transformX: number;
  transformY: number;
  transformWidth: number;
  transformHeight: number;
  transformAngle: number;
  transformScale?: number;
};

const DesignerSlideEditor = ({
  infiniteViewerRef,
  zoom,
  editingAreaRef,
}: {
  infiniteViewerRef: React.RefObject<InfiniteViewer>;
  zoom: number;
  editingAreaRef: React.RefObject<HTMLDivElement>;
}) => {
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const slide = useDesignerDataContext(selectedSlideSelector);
  const onElementsDelete = useDesignerDataContext(c => c.handleElementsDelete);
  const onElementsDuplicate = useDesignerDataContext(c => c.handleKeyBoardDuplicate);
  const lessonAttachments = useDesignerDataContext(attachmentsSelector);

  const setSelectedElements = useDesignerUIContext(
    context => context.setSelectedElements,
  );

  const selectedElements = useDesignerUIContext(
    context => context.state.selectedElements,
  );
  const handleElementsChange = useDesignerDataContext(
    context => context.handleElementsChange,
  );

  const gameId = useDesignerDataContext(
    c => c.state.localTemplate?.content.activeGame?.id,
  );

  const editingTextElementId = useDesignerUIContext(
    context => context.state.textElementIdBeingEdited,
  );
  const selectedRichTextEditors = useDesignerUIContext(
    context => context.state.selectedRichTextEditors,
  );
  const elementsToRender = useDesignerDataContext(elementsToRenderSelector);

  const handleElementsDelete = useCallback(
    (elements: SlideElement[]) => {
      // Reset editing and transform state when deleting an element
      onElementsDelete(elements);
      dispatch({ type: DesignerUIStateActions.deselectElements });
    },
    [dispatch, onElementsDelete],
  );

  if (!slide) return <div />;
  return (
    <NoOverflowContainer ref={editingAreaRef}>
      <ZoomableContainer>
        <ProxySlideToolbar>
          {selectedElements && (
            <SlideEditorToolbar
              selectedElements={selectedElements}
              onElementsChange={handleElementsChange}
              editingTextElementId={editingTextElementId}
              selectedRichTextEditors={selectedRichTextEditors}
            />
          )}
        </ProxySlideToolbar>

        <ProxySlideEditorSurface
          elementsToRender={elementsToRender}
          aspect={ASPECT}
          gameId={gameId}
          onElementsDuplicate={onElementsDuplicate}
          setSelectedElements={setSelectedElements}
          selectedElements={selectedElements}
          onElementsChange={handleElementsChange}
          onElementsDelete={handleElementsDelete}
          editingTextElementId={editingTextElementId}
          infiniteViewerRef={infiniteViewerRef}
          zoom={zoom}
          slide={slide}
          containerRef={editingAreaRef}
          lessonAttachments={lessonAttachments}
        />
      </ZoomableContainer>
    </NoOverflowContainer>
  );
};

const NoOverflowContainer = styled.div`
  flex: 1;
  height: 100%;
  overflow: hidden;
  width: 100%;
`;

const ZoomableContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const ProxySlideToolbar = styled.div`
  position: absolute;
  top: 5px;
  left: 1px;
  width: calc(100% - 20px);
  border-radius: 15px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  z-index: ${zIndexes('CREATOR_SLIDE_EDITOR_TOOLBAR')};
`;
export default DesignerSlideEditor;
